"use client";

import { ApiErrorState } from "@/components/error/api-error-state";
import { useAppSelector } from "@/redux/hooks";
import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import { filterUmrahResult } from "../../lib/filter-umrah-result";
import { toUmrahSearchQueryArgs } from "../../lib/umrah-formatter-helper";
import { useUmrahFilter } from "../../state/extract-umrah-filter-slice";
import { useSearchUmrahQuery } from "../../api/umrah-search-api";
import { IUmrahSearchQueryParams } from "../../types/umrah-search-type";
import { UmrahFilterSidebar } from "../filters/umrah-filter-sidebar";
import { UmrahCardLoadingSkeleton } from "../loader/umrah-card-loading-skeleton";
import UmrahListCard from "./umrah-list-card";

type UmrahSearchProps = {
  decoded: IUmrahSearchQueryParams;
};

export default function UmrahSearch({ decoded }: UmrahSearchProps) {
  const { selectedOption } = useAppSelector(useUmrahFilter);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const searchQueryArgs = useMemo(
    () => toUmrahSearchQueryArgs(decoded),
    [decoded],
  );

  const { currentData, error, isLoading, isFetching, isError, refetch } =
    useSearchUmrahQuery(searchQueryArgs);

  const umrahList = currentData?.data;
  const result = useMemo(
    () => filterUmrahResult(umrahList, selectedOption),
    [umrahList, selectedOption],
  );

  const handleRangeChange = useCallback((range: { startIndex: number }) => {
    const shouldShow = range.startIndex > 4;
    setShowBackToTop((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  const resultsRootRef = useRef<HTMLDivElement | null>(null);

  const renderItem = useCallback(
    (index: number) => {
      const umrah = result[index];
      if (!umrah) return null;

      return (
        <div className="pt-4">
          <UmrahListCard umrah={umrah} searchData={decoded} />
        </div>
      );
    },
    [decoded, result],
  );

  const virtuosoComponents = useMemo(
    () => ({
      Footer: () =>
        result.length > 0 ? (
          <div className="flex justify-center py-4 text-sm text-muted-foreground">
            No more data
          </div>
        ) : null,
    }),
    [result.length],
  );

  useEffect(() => {
    if ((!isLoading || !isFetching) && !isError && result.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isLoading, isFetching, isError, result.length]);

  if (isError)
    return (
      <ApiErrorState
        error={error}
        title="Error fetching umrah packages"
        onRetry={() => void refetch()}
        className="container min-h-[50vh] py-12"
      />
    );

  return (
    <section className="container-wrapper">
      <div className="container px-0 relative flex w-full flex-1 flex-col lg:flex-row lg:items-start">
        <UmrahFilterSidebar isLoading={isLoading || isFetching} />

        <div className="flex-1 overflow-hidden">
          <div className="md:mt-4">
            <div className="relative lg:ml-4 mr-1">
              <div className="h-0 " />
              {isLoading || isFetching ? (
                <div className="mt-4 space-y-4 pr-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i}>
                      <UmrahCardLoadingSkeleton />
                    </div>
                  ))}
                </div>
              ) : result.length === 0 ? (
                <div className="rounded-xl border bg-background p-8 text-center">
                  <h3 className="text-base font-semibold">No packages found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Try adjusting filters or search with another package
                    keyword.
                  </p>
                </div>
              ) : (
                <div className="" ref={resultsRootRef}>
                  <Virtuoso
                    useWindowScroll
                    data={result}
                    overscan={200}
                    rangeChanged={handleRangeChange}
                    itemContent={renderItem}
                    components={virtuosoComponents}
                  />
                </div>
              )}
              {showBackToTop && (
                <button
                  type="button"
                  aria-label="Back to top"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  className="fixed bottom-6 right-6 z-40 rounded-full bg-primary text-primary-foreground shadow-lg p-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
